import { snowflakeApi } from '../utils/api';

export interface WorkflowResultData {
  client_id: string;
  client_name: string;
  workflow_id: string;
  workflow_name: string;
  results_data: Record<string, any>; // Completely dynamic JSON object
}

export interface SerpResultData {
  batch_id: string;
  keywords: string[];
  cities: string[];
  domains: string[];
  additional_metadata?: Record<string, any>;
}

export interface DataForSeoResultData {
  batch_id: string;
  location_code: number;
  language_code: string;
  keywords: string[];
  keyword_results: Record<string, any>[]; // Raw DataForSEO API results
  processing_metadata: Record<string, any>;
  blob_urls?: Record<string, string>;
  workflow_context?: Record<string, any>;
  date_from?: string;
  date_to?: string;
  client_id?: string;
  client_name?: string;
}

export interface ClientFile {
  id?: number;
  client_id: string;
  client_name?: string;
  filename: string;
  file_path: string; // Full blob URL including domain
  file_size?: number;
  file_type?: string;
  file_category?: string;
  load_status?: string;
  created_at?: string;
  date_from?: string; // Start date of data range
  date_to?: string; // End date of data range
}

export interface GuardrailSessionResponse {
  token: string;
  base_url: string;
  public_id: string;
  profile_id?: string; // Optional profile_id (from profile_data_copilot or profile_cortex_agent)
}

export interface SnowflakeResponse {
  success: boolean;
  message: string;
  data?: any;
  error?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

export interface ClientSyncData {
  client_id: number;
  client_code: string;
  name: string;
  industry?: string;
  domains?: string;
  is_active: boolean;
}

export interface LoadKeywordsRequest {
  client_id: string;
  workflow_id: string;
}

export class SnowflakeService {
  private static instance: SnowflakeService;

  private constructor() {}

  static getInstance(): SnowflakeService {
    if (!SnowflakeService.instance) {
      SnowflakeService.instance = new SnowflakeService();
    }
    return SnowflakeService.instance;
  }

  /**
   * Save workflow results to Snowflake
   */
  async saveWorkflowResult(workflowData: WorkflowResultData): Promise<SnowflakeResponse> {
    try {
      console.log('Saving workflow result to Snowflake:', workflowData);
      
      const response = await snowflakeApi.post('/api/v1/workflows/', workflowData);
      
      return {
        success: true,
        message: 'Workflow result saved successfully to Snowflake',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error saving workflow result to Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to save workflow result to Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Check readiness of harmonized FACT_OUTPUT_SERP_TOOL for a client/workflow
   */
  async checkHarmonizedReadiness(clientId: string, workflowId: string): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/harmonized/readiness/${clientId}/${workflowId}`);
      return {
        success: true,
        message: 'Readiness checked',
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to check readiness',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get workflow result from Snowflake
   */
  async getWorkflowResult(workflowId: string): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/workflows/${workflowId}`);
      
      return {
        success: true,
        message: 'Workflow result retrieved successfully',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error getting workflow result from Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to get workflow result from Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get workflow module statuses (used to derive latest module timestamps)
   */
  async getWorkflowModuleStatus(workflowId: string): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/workflows/${workflowId}/module-status`);
      return {
        success: true,
        message: 'Workflow module status fetched',
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to fetch workflow module status',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get all workflow results for a client
   */
  async getClientWorkflowResults(clientId: string): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/workflows/client/${clientId}`);
      
      return {
        success: true,
        message: 'Client workflow results retrieved successfully',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error getting client workflow results from Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to get client workflow results from Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get all workflow results from Snowflake
   */
  async getAllWorkflowResults(): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get('/api/v1/workflows/');
      
      return {
        success: true,
        message: 'All workflow results retrieved successfully',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error getting all workflow results from Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to get all workflow results from Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Save SERP results to Snowflake
   */
  async saveSerpResult(serpData: SerpResultData): Promise<SnowflakeResponse> {
    try {
      console.log('Saving SERP result to Snowflake:', serpData);
      
      const response = await snowflakeApi.post('/api/v1/serp-results/', serpData);
      
      return {
        success: true,
        message: 'SERP result saved successfully to Snowflake',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error saving SERP result to Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to save SERP result to Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get SERP result from Snowflake by batch ID
   */
  async getSerpResult(batchId: string): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/serp-results/${batchId}`);
      
      return {
        success: true,
        message: 'SERP result retrieved successfully',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error getting SERP result from Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to get SERP result from Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get all SERP results from Snowflake
   */
  async getAllSerpResults(): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get('/api/v1/serp-results/');
      
      return {
        success: true,
        message: 'SERP results retrieved successfully',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error getting SERP results from Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to get SERP results from Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Save DataForSEO results to Snowflake
   */
  async saveDataForSeoResult(dataForSeoData: DataForSeoResultData): Promise<SnowflakeResponse> {
    try {
      console.log('📡 Saving DataForSEO result to Snowflake (simplified pattern):', {
        batch_id: dataForSeoData.batch_id,
        keywords_count: dataForSeoData.keywords.length,
        keyword_results_count: dataForSeoData.keyword_results.length,
        api_url: '/api/v1/dataforseo-results/simplified',
        full_data: dataForSeoData
      });
      
      // Transform to simplified format matching SERP pattern
      const simplifiedData = {
        batch_id: dataForSeoData.batch_id,
        keywords: dataForSeoData.keywords,
        location_code: dataForSeoData.location_code,
        language_code: dataForSeoData.language_code,
        additional_metadata: {
          ...dataForSeoData.processing_metadata,
          ...dataForSeoData.blob_urls,
          keyword_results: dataForSeoData.keyword_results,
          workflow_context: dataForSeoData.workflow_context,
          date_from: dataForSeoData.date_from,
          date_to: dataForSeoData.date_to,
          client_id: dataForSeoData.client_id,
          client_name: dataForSeoData.client_name
        }
      };
      
      const response = await snowflakeApi.post('/api/v1/dataforseo-results/simplified', simplifiedData);
      
      console.log('✅ Snowflake API response:', response.data);
      
      return {
        success: true,
        message: 'DataForSEO result saved successfully to Snowflake',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('❌ Error saving DataForSEO result to Snowflake:', error);
      console.error('Error response:', error.response);
      
      return {
        success: false,
        message: 'Failed to save DataForSEO result to Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get DataForSEO result from Snowflake by batch ID
   */
  async getDataForSeoResult(batchId: string): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/dataforseo-results/${batchId}`);
      
      return {
        success: true,
        message: 'DataForSEO result retrieved successfully',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error getting DataForSEO result from Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to get DataForSEO result from Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get all DataForSEO results from Snowflake
   */
  async getAllDataForSeoResults(): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get('/api/v1/dataforseo-results/');
      
      return {
        success: true,
        message: 'DataForSEO results retrieved successfully',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error getting DataForSEO results from Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to get DataForSEO results from Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get latest DataForSEO result for a workflow (from dataforseo_results)
   */
  async getLatestDataForSeoResultForWorkflow(workflowId: string): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/dataforseo-results/latest/${workflowId}`);
      return {
        success: true,
        message: 'Latest DataForSEO result retrieved successfully',
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to get latest DataForSEO result for workflow',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Check if keywords are ready for processing for a specific client workflow
   */
  async checkKeywordReadiness(clientId: string, workflowId: string): Promise<SnowflakeResponse> {
    try {
      console.log(`🌐 Frontend Service: Making request to /api/v1/keywords/readiness/${clientId}/${workflowId}`);
      const response = await snowflakeApi.get(`/api/v1/keywords/readiness/${clientId}/${workflowId}`);
      
      console.log(`📥 Frontend Service: Raw axios response for client ${clientId}, workflow ${workflowId}:`, response.data);
      
      // The backend returns: { status: "success", data: { ready: true, ... }, message: "..." }
      // We need to extract the nested data
      if (response.data && response.data.status === 'success' && response.data.data) {
        console.log(`✅ Frontend Service: Extracting nested data for client ${clientId}, workflow ${workflowId}:`, response.data.data);
        return {
          success: true,
          message: response.data.message || 'Keyword readiness status retrieved successfully',
          data: response.data.data  // Extract the nested data
        };
      } else {
        console.log(`❌ Frontend Service: Unexpected response structure for client ${clientId}, workflow ${workflowId}:`, response.data);
        return {
          success: false,
          message: 'Unexpected response format from backend',
          error: 'Backend returned unexpected response structure'
        };
      }
      
    } catch (error: any) {
      console.error('Error checking keyword readiness from Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to check keyword readiness from Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Load keywords from Snowflake and save them to blob storage as CSV
   */
  async loadKeywordsToBlob(request: LoadKeywordsRequest): Promise<SnowflakeResponse> {
    try {
      console.log(`🔄 Frontend Service: Loading keywords to blob for client ${request.client_id}, workflow ${request.workflow_id}`);
      
      const response = await snowflakeApi.post('/api/v1/keywords/load', request);
      
      console.log(`📥 Frontend Service: Load keywords response:`, response.data);
      
      // The backend returns: { status: "success", data: { success: true, ... }, message: "..." }
      if (response.data && response.data.status === 'success' && response.data.data) {
        return {
          success: response.data.data.success || false,
          message: response.data.message || 'Keywords loaded to blob storage',
          data: response.data.data
        };
      } else {
        return {
          success: false,
          message: 'Unexpected response format from backend',
          error: 'Backend returned unexpected response structure'
        };
      }
      
    } catch (error: any) {
      console.error('Error loading keywords to blob:', error);
      
      return {
        success: false,
        message: 'Failed to load keywords to blob storage',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Sync client data to Snowflake
   */
  async syncClient(clientData: ClientSyncData): Promise<SnowflakeResponse> {
    try {

      
      const response = await snowflakeApi.post('/api/v1/clients/sync', clientData);
      
      return {
        success: true,
        message: 'Client synced successfully to Snowflake',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error syncing client to Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to sync client to Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get client from Snowflake by database ID
   */
  async getClient(clientId: number): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/clients/${clientId}`);
      
      return {
        success: true,
        message: 'Client retrieved successfully from Snowflake',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error getting client from Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to get client from Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get client from Snowflake by client code
   */
  async getClientByCode(clientCode: string): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get(`/api/v1/clients/code/${clientCode}`);
      
      return {
        success: true,
        message: 'Client retrieved successfully from Snowflake',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error getting client by code from Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to get client by code from Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get all clients from Snowflake
   */
  async getAllClients(limit?: number, offset?: number): Promise<SnowflakeResponse> {
    try {
      const params: Record<string, any> = {};
      if (limit) params.limit = limit;
      if (offset) params.offset = offset;
      
      const response = await snowflakeApi.get('/api/v1/clients/', { params });
      
      return {
        success: true,
        message: 'Clients retrieved successfully from Snowflake',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error getting all clients from Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to get all clients from Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Update client activity status in Snowflake
   */
  async updateClientActivity(clientId: number, isActive: boolean): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.put(`/api/v1/clients/${clientId}/activity`, {
        is_active: isActive
      });
      
      return {
        success: true,
        message: 'Client activity status updated successfully in Snowflake',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error updating client activity in Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to update client activity in Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get Qlik configuration from backend
   */
  async getQlikConfig(): Promise<SnowflakeResponse> {
    try {
      console.log('🔄 Frontend Service: Fetching Qlik configuration from backend');
      
      const response = await snowflakeApi.get('/api/v1/qlik/config');
      
      console.log('📥 Frontend Service: Qlik config response:', response.data);
      
      return {
        success: true,
        data: response.data,
        message: 'Qlik configuration retrieved successfully',
      };
    } catch (error) {
      console.error('Error getting Qlik configuration from backend:', error);
      return {
        success: false,
        data: null,
        message: 'Failed to get Qlik configuration from backend',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Execute Qlik automation for a specific client ID
   */
  async executeQlikAutomation(clientId: string, additionalData?: Record<string, any>): Promise<SnowflakeResponse> {
    try {
      console.log(`🔄 Frontend Service: Executing Qlik automation for client ${clientId}`);
      
      const requestData = {
        client_id: clientId,
        additional_data: additionalData
      };
      
      const response = await snowflakeApi.post('/api/v1/qlik/execute', requestData);
      
      console.log(`📥 Frontend Service: Qlik automation response for client ${clientId}:`, response.data);
      
      return {
        success: true,
        data: response.data,
        message: 'Qlik automation executed successfully',
      };
    } catch (error) {
      console.error(`Error executing Qlik automation for client ${clientId}:`, error);
      return {
        success: false,
        data: null,
        message: 'Failed to execute Qlik automation',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Create a guardrail session
   */
  async createGuardrailSession(): Promise<GuardrailSessionResponse> {
    const response = await snowflakeApi.post('/api/v1/auth/guardrail-session/create');
    return response.data;
  }

  /**
   * Create a guardrail session for Copilot
   */
  async createGuardrailCopilotSession(): Promise<GuardrailSessionResponse> {
    const response = await snowflakeApi.post('/api/v1/auth/guardrail-copilot-session/create');
    return response.data;
  }

  /**
   * Get Guardrail chat config (base_url + profile_cortex_agent profile_id).
   * Uses the user's authguard token from localStorage instead of generating an app token.
   */
  async getGuardrailChatConfig(): Promise<GuardrailSessionResponse> {
    const response = await snowflakeApi.get('/api/v1/auth/guardrail-chat-config');
    const { base_url, profile_id } = response.data;
    const token = typeof window !== 'undefined'
      ? (localStorage.getItem('authguard_access_token') ?? '')
      : '';
    return { token, base_url, public_id: '', profile_id };
  }

  /**
   * LEGACY: Create a guardrail conversation (for Copilot - uses Copilot app token)
   * Only used by legacy Explore.tsx page. New ExploreStep.tsx uses createGuardrailTotalSerpConversation().
   */
  async createGuardrailConversation(conversationId: string): Promise<any> {
    const response = await snowflakeApi.post('/api/v1/auth/guardrail-copilot-conversation/create', {
      conversation_id: conversationId
    });
    return response.data;
  }

  /**
   * Create a guardrail conversation using TotalSERP app token (for Free Chat)
   */
  async createGuardrailTotalSerpConversation(conversationId: string): Promise<any> {
    const response = await snowflakeApi.post('/api/v1/auth/guardrail-conversation/create', {
      conversation_id: conversationId
    });
    return response.data;
  }

  /**
   * Create a Guardrail conversation using the user's own authguard token.
   * The Authorization header is attached automatically by the request interceptor.
   */
  async createGuardrailConversationWithUserToken(conversationId: string): Promise<any> {
    const response = await snowflakeApi.post('/api/v1/auth/guardrail-conversation/create-with-user-token', {
      conversation_id: conversationId,
    });
    return response.data;
  }

  /**
   * Get analytics questions (uses instruction name from app settings)
   */
  async getAnalyticsQuestions(): Promise<SnowflakeResponse> {
    try {
      console.log('🔄 Frontend Service: Fetching analytics questions');
      
      const response = await snowflakeApi.get('/api/v1/application-settings/analytics-questions');
      
      console.log('📥 Frontend Service: Analytics questions response:', response.data);
      
      return {
        success: true,
        message: 'Analytics questions retrieved successfully',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error getting analytics questions:', error);
      
      return {
        success: false,
        message: 'Failed to get analytics questions',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }
 
  /**
   * Get guardrail instruction by name
   */
  async getGuardrailInstruction(instructionName: string): Promise<SnowflakeResponse> {
    try {
      console.log(`🔄 Frontend Service: Fetching guardrail instruction: ${instructionName}`);
      
      const response = await snowflakeApi.get(`/api/v1/auth/guardrail/instructions/${instructionName}`);
      
      console.log(`📥 Frontend Service: Guardrail instruction response for ${instructionName}:`, response.data);
      
      return {
        success: true,
        message: 'Guardrail instruction retrieved successfully',
        data: response.data
      };
      
    } catch (error: any) {
      console.error(`Error getting guardrail instruction ${instructionName}:`, error);
      
      return {
        success: false,
        message: 'Failed to get guardrail instruction',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Update guardrail instruction by name
   */
  async updateGuardrailInstruction(instructionName: string, content: string): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.put(`/api/v1/auth/guardrail/instructions/${instructionName}`, { content });
      return {
        success: true,
        message: 'Guardrail instruction updated successfully',
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to update guardrail instruction',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Update guardrail instruction by id (new API)
   */
  async updateGuardrailInstructionById(
    instructionId: number,
    payload: { title?: string; behavior: string; behaviorTypeId?: number; isPublic?: boolean }
  ): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.put(`/api/v1/auth/guardrail/instructions/id/${instructionId}`, payload);
      return {
        success: true,
        message: 'Guardrail instruction updated successfully',
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to update guardrail instruction by id',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Send analytics question chat message (uses profile_cortex_agent)
   */
  async sendAnalyticsChatMessage(request: { message: string; metadata?: Record<string, any>; [key: string]: any }): Promise<SnowflakeResponse> {
    try {
      // Use 30 minutes timeout for guardrail chat requests (1800000ms = 30 minutes)
      const response = await snowflakeApi.post('/api/v1/auth/guardrail/analytics-chat', request, {
        timeout: 1800000 // 30 minutes
      });
           
      return {
        success: true,
        message: 'Analytics chat message sent successfully',
        data: response.data
      };
      
    } catch (error: any) {
      
      return {
        success: false,
        message: 'Failed to send analytics chat message',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Send generic guardrail chat message
   */
  async sendGuardrailChatMessage(request: { message: string; metadata?: Record<string, any>; [key: string]: any }): Promise<SnowflakeResponse> {
    try {
      // Use 30 minutes timeout for guardrail chat requests (1800000ms = 30 minutes)
      const response = await snowflakeApi.post('/api/v1/auth/guardrail/chat', request, {
        timeout: 1800000 // 30 minutes
      });
           
      return {
        success: true,
        message: 'Chat message sent successfully',
        data: response.data
      };
      
    } catch (error: any) {
      
      return {
        success: false,
        message: 'Failed to send chat message',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Health check for Snowflake service
   */
  async healthCheck(): Promise<SnowflakeResponse> {
    try {
      const response = await snowflakeApi.get('/health');
      
      return {
        success: true,
        message: 'Snowflake service is healthy',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Snowflake health check failed:', error);
      
      return {
        success: false,
        message: 'Snowflake service health check failed',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get SERP workflow run history for a specific client and workflow from both sources
   */
  async getSerpWorkflowHistory(clientId: string, workflowId: string, limit: number = 10): Promise<SnowflakeResponse> {
    try {
      console.log(`Fetching SERP workflow history from both Snowflake and blob storage: ${clientId}/${workflowId}`);
      
      // Call both endpoints in parallel
      const [snowflakeResult, blobResult] = await Promise.allSettled([
        snowflakeApi.get(`/api/v1/serp-results/workflow-history/${clientId}/${workflowId}?limit=${limit}`).catch(e => ({ error: e })),
        snowflakeApi.get(`/api/v1/serp-results/workflow-history-blob/${clientId}/${workflowId}?limit=${limit}`).catch(e => ({ error: e }))
      ]);
      
      // Process Snowflake results
      let snowflakeRuns: any[] = [];
      let snowflakeError = null;
      if (snowflakeResult.status === 'fulfilled' && !(snowflakeResult.value as any)?.error) {
        const runs = (snowflakeResult.value as any)?.data?.data?.runs || [];
        snowflakeRuns = runs.map((run: any) => ({ ...run, dataSource: 'snowflake' }));
        console.log(`Found ${snowflakeRuns.length} runs from Snowflake`);
      } else {
        snowflakeError = snowflakeResult.status === 'rejected' 
          ? (snowflakeResult as PromiseRejectedResult).reason 
          : (snowflakeResult.value as any)?.error;
        console.warn('Snowflake endpoint failed:', snowflakeError?.message || snowflakeError);
      }
      
      // Process blob results
      let blobRuns: any[] = [];
      let blobError = null;
      if (blobResult.status === 'fulfilled' && !(blobResult.value as any)?.error) {
        const runs = (blobResult.value as any)?.data?.data?.runs || [];
        blobRuns = runs.map((run: any) => ({ ...run, dataSource: 'blob' }));
        console.log(`Found ${blobRuns.length} runs from blob storage`);
      } else {
        blobError = blobResult.status === 'rejected' 
          ? (blobResult as PromiseRejectedResult).reason 
          : (blobResult.value as any)?.error;
        console.warn('Blob storage endpoint failed:', blobError?.message || blobError);
      }
      
      // Merge and deduplicate results by batch_id, prioritizing Snowflake data
      const mergedRuns = new Map();
      
      // Add blob runs first
      blobRuns.forEach(run => {
        mergedRuns.set(run.batch_id, { ...run, inBlob: true, inSnowflake: false });
      });
      
      // Add/update with Snowflake runs (they take priority for metadata)
      snowflakeRuns.forEach(run => {
        if (mergedRuns.has(run.batch_id)) {
          // Run exists in both sources
          mergedRuns.set(run.batch_id, { 
            ...mergedRuns.get(run.batch_id), 
            ...run, 
            dataSource: 'both',
            inSnowflake: true 
          });
        } else {
          // Run only in Snowflake
          mergedRuns.set(run.batch_id, { ...run, inBlob: false, inSnowflake: true });
        }
      });
      
      const allRuns = Array.from(mergedRuns.values())
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, limit);
      
      if (allRuns.length === 0) {
        return {
          success: false,
          message: 'No SERP workflow history found in either Snowflake or blob storage',
          error: `Snowflake: ${snowflakeError?.message || 'No data'}; Blob: ${blobError?.message || 'No data'}`
        };
      }
      
      // Determine primary data source for messaging
      const snowflakeCount = allRuns.filter(r => r.inSnowflake).length;
      const blobOnlyCount = allRuns.filter(r => r.inBlob && !r.inSnowflake).length;
      
      let message = `Found ${allRuns.length} SERP runs`;
      if (snowflakeCount > 0 && blobOnlyCount > 0) {
        message += ` (${snowflakeCount} in Snowflake, ${blobOnlyCount} blob-only)`;
      } else if (snowflakeCount > 0) {
        message += ` from Snowflake`;
      } else {
        message += ` from blob storage only`;
      }
      
      return {
        success: true,
        message,
        data: {
          success: true,
          data: {
            client_id: clientId,
            workflow_id: workflowId,
            total_runs: allRuns.length,
            runs: allRuns,
            source: 'combined',
            snowflake_count: snowflakeCount,
            blob_only_count: blobOnlyCount
          }
        }
      };
      
    } catch (error: any) {
      console.error('Error fetching SERP workflow history:', error);
      return {
        success: false,
        message: 'Failed to fetch SERP workflow history from both sources',
        error: error.message || 'Unknown error'
      };
    }
  }

  /**
   * Sync a SERP result from blob storage to Snowflake
   */
  async syncSerpBlobToSnowflake(clientId: string, workflowId: string, batchId: string): Promise<SnowflakeResponse> {
    try {
      console.log(`Syncing SERP result ${batchId} from blob to Snowflake`);
      
      const response = await snowflakeApi.post(`/api/v1/serp-results/sync-blob-to-snowflake/${clientId}/${workflowId}/${batchId}`);
      
      return {
        success: true,
        message: `SERP result ${batchId} synced successfully to Snowflake`,
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error syncing SERP result to Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to sync SERP result to Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get latest SERP keywords for a workflow in localStorage format
   */
  async getLatestSerpKeywords(clientId: string, workflowId: string): Promise<SnowflakeResponse> {
    try {
      console.log(`Getting latest SERP keywords for client ${clientId}, workflow ${workflowId}`);
      
      const response = await snowflakeApi.get(`/api/v1/serp-results/latest-keywords/${clientId}/${workflowId}`);
      
      return {
        success: true,
        message: 'Latest SERP keywords retrieved successfully',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('Error getting latest SERP keywords from Snowflake:', error);
      
      return {
        success: false,
        message: 'Failed to get latest SERP keywords from Snowflake',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * NEW V2: Ingest SERP keyword results from blob storage into Snowflake
   * This is Step 2 of the new 2-step architecture
   */
  async ingestSerpKeywordsFromBlob(ingestionRequest: {
    batch_id: string;
    client_id: string;
    client_name: string;
    container_name: string;
    blob_prefix: string;
    workflow_id?: string;
    domains?: string[];
  }): Promise<SnowflakeResponse> {
    try {
      console.log('🔄 Starting SERP keyword ingestion from blob storage:', {
        batch_id: ingestionRequest.batch_id,
        client_id: ingestionRequest.client_id,
        container_name: ingestionRequest.container_name,
        blob_prefix: ingestionRequest.blob_prefix,
        workflow_id: ingestionRequest.workflow_id
      });
      
      const response = await snowflakeApi.post('/api/v1/serp-keyword-results/ingest-from-blob', ingestionRequest);
      
      console.log('✅ SERP keyword ingestion completed:', response.data);
      
      return {
        success: true,
        message: 'SERP keyword results ingested successfully from blob storage',
        data: response.data
      };
      
    } catch (error: any) {
      console.error('❌ Error ingesting SERP keywords from blob storage:', error);
      console.error('Error response:', error.response);
      
      return {
        success: false,
        message: 'Failed to ingest SERP keyword results from blob storage',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  // Get workflow conversations grouped by question_id (latest conversation per question)
  async getWorkflowConversations(workflowId: string): Promise<ApiResponse<Record<string, { conversation_id: string; error_message: string | null }>>> {
    try {
      console.log(`🔍 Getting workflow conversations for workflow: ${workflowId}`);
      
      const response = await snowflakeApi.get(`/api/v1/auth/workflow/${workflowId}/conversations`);
      
      console.log('✅ Successfully retrieved workflow conversations:', response.data);
      
      return {
        success: true,
        data: response.data.data,
        message: 'Workflow conversations retrieved successfully'
      };
    } catch (error: any) {
      console.error('❌ Error getting workflow conversations:', error);
      console.error('Error response:', error.response);
      
      return {
        success: false,
        message: 'Failed to get workflow conversations',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  // Save a workflow conversation without sending a chat message
  async saveWorkflowConversation(workflowId: string, questionId: string | number, conversationId: string): Promise<SnowflakeResponse> {
    try {
      console.log(`💾 Saving workflow conversation: workflow=${workflowId}, question=${questionId}, conversation=${conversationId}`);
      
      const response = await snowflakeApi.post(`/api/v1/auth/workflow/${workflowId}/conversations`, {
        question_id: questionId,
        conversation_id: conversationId
      });
      
      console.log('✅ Successfully saved workflow conversation:', response.data);
      
      return {
        success: true,
        message: 'Workflow conversation saved successfully',
        data: response.data
      };
    } catch (error: any) {
      console.error('❌ Error saving workflow conversation:', error);
      
      return {
        success: false,
        message: 'Failed to save workflow conversation',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }

  /**
   * Get the full conversation history for the current user, a workflow, and a question.
   * Returns entries sorted newest-first (backend already handles ordering).
   */
  async getConversationHistory(workflowId: string, questionId: string | number): Promise<import('../types').ConversationHistoryItem[]> {
    try {
      const response = await snowflakeApi.get(
        `/api/v1/auth/workflow/${workflowId}/conversations/history`,
        { params: { question_id: questionId } }
      );
      return response.data ?? [];
    } catch (error: any) {
      console.error('Error fetching conversation history:', error);
      return [];
    }
  }

  /**
   * Get workflow status metrics from Snowflake tables
   */
  async getWorkflowStatusMetrics(workflowId: string): Promise<SnowflakeResponse> {
    try {
      console.log(`🔍 Getting workflow status metrics for workflow: ${workflowId}`);
      
      // This endpoint is in the module-snowflake service under /api/v1/snowflake prefix
      const response = await snowflakeApi.get(`/api/v1/snowflake/workflow/${workflowId}/status-metrics`);
      
      console.log('✅ Successfully retrieved workflow status metrics:', response.data);
      
      return {
        success: true,
        message: 'Workflow status metrics retrieved successfully',
        data: response.data
      };
    } catch (error: any) {
      console.error('❌ Error getting workflow status metrics:', error);
      console.error('Error response:', error.response);
      
      return {
        success: false,
        message: 'Failed to get workflow status metrics',
        error: error.response?.data?.detail || error.message || 'Unknown error'
      };
    }
  }
}

export const snowflakeService = SnowflakeService.getInstance();